// BUG-093 — corpId(=user.companyId) → managerGroupId(=rayteams-manager-group._id) 변환
// 헬퍼. LicenseAction.GetManagerGroupIdByCompanyId 와 동일 데이터 모델을 가지나 axios
// 등 무거운 의존성을 lib 경계로 끌어들이지 않도록 slim 버전으로 분리.
//
// LicenseAction 의 동등 함수는 CompanyAction.GetParents 기반의 branch-parent fallback
// 까지 포함하나, 본 슬림 버전은 직접 scan 만 시도한다. branch fallback 이 필요한
// 케이스가 SmartDent 흐름에서 관찰되면 그 시점에 통합 검토 (IMP 후속).
//
// IMP-426: companyId 별 in-flight promise + TTL(5분) 캐시로 반복 scan 을 제거한다.
// AMBIGUOUS_MANAGER_GROUP(null) 도 정상 해석 결과로 캐시한다.
//
// PR #539 리뷰 반영:
// - expiresAt 는 promise 발행 시점이 아니라 **settle 시점**에 찍는다(entry.expiresAt=null
//   동안은 in-flight 로 간주해 항상 hit). 발행 시점 기준이면 하위 조회가 TTL 보다 오래 걸릴 때
//   아직 끝나지 않은 조회를 캐시가 먼저 "만료"시켜 같은 클릭 내 dedup 이 무효화된다.
// - evict 는 호출자(`GetManagerGroupIdByCompanyId`)의 `.then(_, onRejected)` 에서, 캐시가
//   여전히 이 entry 를 가리킬 때만 삭제한다(소유권 확인) — TTL 만료 후 이미 시작된 새 조회를
//   뒤늦게 실패한 예전 조회가 지우는 것을 방지.
// - 쓰기 경로(AllocateToDealer/RevokeFromDealer/IssueQr)는 mg 재배정 직후에도 최신 조회가
//   필요해 `{ fresh: true }` 로 캐시를 우회한다(결과는 여전히 캐시에 반영해 후속 read 는 혜택).
import { DoDB } from './ddb';

const TABLE_RAYTEAMS_MANAGER_GROUP = 'rayteams-manager-group';
const CACHE_TTL_MS = 5 * 60 * 1000; // BE treeCache.js(IMP-122) 와 동일 자릿수 — 세션 중 mg 재배정 반영

const cache = new Map(); // companyId -> { promise: Promise<string|null>, expiresAt: number|null }

const scanManagerGroupId = async (companyId) => {
    const list = await DoDB('scan', {
        TableName: TABLE_RAYTEAMS_MANAGER_GROUP,
        FilterExpression: 'contains(#companyIds, :companyId)',
        ExpressionAttributeNames: { '#companyIds': 'companyIds' },
        ExpressionAttributeValues: { ':companyId': companyId },
    });
    if (!Array.isArray(list) || list.length === 0) return null;

    // PR #187 리뷰 Major #2 (Security) — cross-tenant 방어.
    // 동일 companyId 가 둘 이상의 manager-group `companyIds[]` 에 노출되면 (데이터
    // 정합성 오류·마이그레이션 잔재·멀티 소속) `list[0]` first-match 가 임의 그룹의
    // 권한 컨텍스트로 사용될 수 있다. 명시적으로 null 반환 + 로그하여 호출자가
    // CORP_ALLOCATION_NOT_FOUND 로 안전 차단되도록 한다. GSI 도입은 IMP-110 후속.
    if (list.length > 1) {
        console.error(
            '[lib.managerGroupResolver] AMBIGUOUS_MANAGER_GROUP — cross-tenant 위험 차단',
            { companyId, matchedCount: list.length, ids: list.map((l) => l?._id) },
        );
        return null;
    }

    return list[0]?._id ?? null;
};

export const GetManagerGroupIdByCompanyId = async (companyId, { fresh = false } = {}) => {
    if (!companyId || typeof companyId !== 'string') return null;

    if (!fresh) {
        const cached = cache.get(companyId);
        if (cached && (cached.expiresAt === null || cached.expiresAt > Date.now())) {
            return cached.promise;
        }
    }

    const entry = { promise: null, expiresAt: null }; // expiresAt=null → in-flight, 항상 hit
    entry.promise = scanManagerGroupId(companyId).then(
        (result) => {
            entry.expiresAt = Date.now() + CACHE_TTL_MS;
            return result;
        },
        (error) => {
            // ddb.js 의 read fallback 에 의해 현재는 도달하지 않지만, 그 전제가 깨져도
            // "일시 오류는 캐시하지 않음" 불변식을 지킨다. 외부 계약(never throw)은 유지.
            console.error('[lib.managerGroupResolver]', error);
            if (cache.get(companyId) === entry) cache.delete(companyId);
            return null;
        },
    );
    cache.set(companyId, entry);
    return entry.promise;
};

// 모듈 스코프 캐시 초기화 — 테스트 격리(각 테스트 beforeEach)와 Logout(계정 전환 시
// 이전 계정의 mg 매핑 잔존 방지) 양쪽에서 사용하는 공용 API.
export const clearManagerGroupCache = () => cache.clear();
